import React, { useState, useMemo } from "react";
import {
  Search,
  Plus,
  Sparkles,
  Calendar,
  CheckCircle2,
  HelpCircle,
  Trash2,
  Edit2,
  Layers,
  ArrowDownToLine,
  Check,
  Loader2,
} from "lucide-react";
import {
  Opportunity,
  Requirement,
  DraftSection,
  ProposalSectionGroup,
  RequirementCategory,
  UserProfile,
} from "../../types";
import { useStaffDirectory } from "../../services/staffService";
import {
  updateSectionAllocation,
  cascadeSectionAssignee,
  autoInitializeDraftSections,
} from "../../services/teamAllocationService";
import { deleteDraftSection, saveDraftSection } from "../../services/draftsService";
import { ExpandableCard } from "../common/ExpandableCard";
import { Modal } from "../common/Modal";
import { EditSectionModal, EditSectionFormData } from "./EditSectionModal";
import { AskSmeModal } from "./AskSmeModal";

interface SectionAllocationMatrixProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  sectionGroups: ProposalSectionGroup[];
  requirements: Requirement[];
  draftSections: DraftSection[];
  selectedMemberEmail?: string | null;
  onClearMemberFilter?: () => void;
  isUnassignedFiltered?: boolean;
  onClearUnassignedFilter?: () => void;
}

export const SectionAllocationMatrix: React.FC<SectionAllocationMatrixProps> = ({
  opportunity,
  user,
  sectionGroups,
  requirements,
  draftSections,
  selectedMemberEmail,
  onClearMemberFilter,
  isUnassignedFiltered,
  onClearUnassignedFilter,
}) => {
  const { staff } = useStaffDirectory({ rfpOnly: true });

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");

  // Active modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<ProposalSectionGroup | null>(null);
  const [confirmDeleteSection, setConfirmDeleteSection] = useState<ProposalSectionGroup | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initSuccessMessage, setInitSuccessMessage] = useState<string | null>(null);

  // SME consultation modal state
  const [activeSmeReq, setActiveSmeReq] = useState<Requirement | null>(null);

  // Cascade in-progress feedback: Map<groupId, boolean>
  const [cascadingMap, setCascadingMap] = useState<Record<string, boolean>>({});
  const [cascadeSuccessMap, setCascadeSuccessMap] = useState<Record<string, boolean>>({});

  // Has virtual groups that can be initialized to Firestore draft_sections
  const hasUninitializedGroups = useMemo(() => {
    return sectionGroups.some((g) => !g.draftSection && g.requirements.length > 0);
  }, [sectionGroups]);

  // Combined filtered groups
  const filteredGroups = useMemo(() => {
    return sectionGroups.filter((group) => {
      // 1. External filter from TeamWorkloadRollup: member spotlight
      if (selectedMemberEmail) {
        const matchesLead =
          group.assignedToEmail?.toLowerCase() === selectedMemberEmail.toLowerCase();
        const matchesReviewer =
          group.reviewerEmail?.toLowerCase() === selectedMemberEmail.toLowerCase();
        if (!matchesLead && !matchesReviewer) return false;
      }

      // 2. External filter: unassigned only
      if (isUnassignedFiltered) {
        if (group.assignedToEmail || group.assignedTo) return false;
      }

      // 3. Category filter
      if (categoryFilter !== "all" && group.category !== categoryFilter) {
        return false;
      }

      // 4. Status filter
      if (statusFilter !== "all" && group.status !== statusFilter) {
        return false;
      }

      // 5. Assignee filter (local dropdown)
      if (assigneeFilter !== "all") {
        if (assigneeFilter === "unassigned") {
          if (group.assignedToEmail || group.assignedTo) return false;
        } else if (group.assignedToEmail?.toLowerCase() !== assigneeFilter.toLowerCase()) {
          return false;
        }
      }

      // 6. Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = group.title.toLowerCase().includes(q);
        const matchesRef = group.sectionRef.toLowerCase().includes(q);
        const matchesNotes = group.notes?.toLowerCase().includes(q) || false;
        const matchesReqs = group.requirements.some(
          (r) => r.title.toLowerCase().includes(q) || r.prompt.toLowerCase().includes(q),
        );
        if (!matchesTitle && !matchesRef && !matchesNotes && !matchesReqs) {
          return false;
        }
      }

      return true;
    });
  }, [
    sectionGroups,
    selectedMemberEmail,
    isUnassignedFiltered,
    categoryFilter,
    statusFilter,
    assigneeFilter,
    searchQuery,
  ]);

  // Handler: Change Drafter / Section Lead
  const handleAssignLead = async (group: ProposalSectionGroup, staffEmail: string) => {
    const selected = staff.find((s) => s.email.toLowerCase() === staffEmail.toLowerCase());
    const assignedTo = selected?.displayName || (staffEmail ? staffEmail.split("@")[0] : undefined);
    const assignedToEmail = staffEmail || undefined;

    if (group.draftSection) {
      await updateSectionAllocation(
        opportunity.id,
        group.draftSection,
        { assignedTo, assignedToEmail },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    } else {
      // Create backing draft section if virtual
      const primaryReq = group.requirements[0];
      await saveDraftSection(
        opportunity.id,
        {
          requirementId: primaryReq?.id || "",
          title: group.title,
          sectionRef: group.sectionRef,
          category: group.category,
          assignedTo,
          assignedToEmail,
          targetWordCount: group.targetWordCount,
          content: "",
          status: "draft",
        },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    }
  };

  // Handler: Change SME Reviewer
  const handleAssignReviewer = async (group: ProposalSectionGroup, reviewerEmail: string) => {
    const selected = staff.find((s) => s.email.toLowerCase() === reviewerEmail.toLowerCase());
    const reviewerName =
      selected?.displayName || (reviewerEmail ? reviewerEmail.split("@")[0] : undefined);
    const revEmail = reviewerEmail || undefined;

    if (group.draftSection) {
      await updateSectionAllocation(
        opportunity.id,
        group.draftSection,
        { reviewerName, reviewerEmail: revEmail },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    } else {
      const primaryReq = group.requirements[0];
      await saveDraftSection(
        opportunity.id,
        {
          requirementId: primaryReq?.id || "",
          title: group.title,
          sectionRef: group.sectionRef,
          category: group.category,
          reviewerName,
          reviewerEmail: revEmail,
          targetWordCount: group.targetWordCount,
          content: "",
          status: "draft",
        },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    }
  };

  // Handler: Cascade drafter to requirements
  const handleCascade = async (group: ProposalSectionGroup) => {
    if (!group.requirements.length || !group.assignedToEmail) return;

    setCascadingMap((prev) => ({ ...prev, [group.id]: true }));
    try {
      const reqIds = group.requirements.map((r) => r.id);
      const email = group.assignedToEmail;
      if (!email) return;
      await cascadeSectionAssignee(opportunity.id, reqIds, {
        displayName: group.assignedTo || email.split("@")[0] || "Assignee",
        email,
      });
      setCascadeSuccessMap((prev) => ({ ...prev, [group.id]: true }));
      setTimeout(() => {
        setCascadeSuccessMap((prev) => ({ ...prev, [group.id]: false }));
      }, 3000);
    } finally {
      setCascadingMap((prev) => ({ ...prev, [group.id]: false }));
    }
  };

  // Handler: Auto-initialize outline
  const handleAutoInit = async () => {
    setIsInitializing(true);
    setInitSuccessMessage(null);
    try {
      const count = await autoInitializeDraftSections(
        opportunity.id,
        requirements,
        draftSections,
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
      setInitSuccessMessage(
        `Successfully initialized ${count} proposal section(s) in drafting outline.`,
      );
      setTimeout(() => setInitSuccessMessage(null), 4000);
    } finally {
      setIsInitializing(false);
    }
  };

  // Handler: Save from EditSectionModal
  const handleSaveSection = async (data: EditSectionFormData) => {
    if (editingSection && editingSection.draftSection) {
      await updateSectionAllocation(
        opportunity.id,
        editingSection.draftSection,
        {
          title: data.title,
          sectionRef: data.sectionRef,
          category: data.category,
          assignedTo: data.assignedTo,
          assignedToEmail: data.assignedToEmail,
          reviewerName: data.reviewerName,
          reviewerEmail: data.reviewerEmail,
          targetWordCount: data.targetWordCount,
          dueDate: data.dueDate,
          notes: data.notes,
        },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    } else {
      // Create new DraftSection
      const primaryReqId = editingSection?.requirements[0]?.id || "";
      await saveDraftSection(
        opportunity.id,
        {
          requirementId: primaryReqId,
          title: data.title,
          sectionRef: data.sectionRef,
          category: data.category,
          assignedTo: data.assignedTo,
          assignedToEmail: data.assignedToEmail,
          reviewerName: data.reviewerName,
          reviewerEmail: data.reviewerEmail,
          targetWordCount: data.targetWordCount,
          dueDate: data.dueDate,
          notes: data.notes,
          content: "",
          status: "draft",
        },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    }
  };

  // Handler: Delete Section
  const handleConfirmDelete = async () => {
    if (!confirmDeleteSection || !confirmDeleteSection.draftSection) return;
    setIsDeleting(true);
    try {
      await deleteDraftSection(opportunity.id, confirmDeleteSection.draftSection.id);
      setConfirmDeleteSection(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const getCategoryColor = (cat: RequirementCategory) => {
    switch (cat) {
      case "technical":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
      case "qualifications":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300";
      case "pricing":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
      case "compliance":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
      case "management":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300";
      default:
        return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300";
    }
  };

  const getStatusBadge = (status: ProposalSectionGroup["status"]) => {
    switch (status) {
      case "approved":
        return (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
            Approved
          </span>
        );
      case "review":
        return (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            In Review
          </span>
        );
      case "draft":
        return (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
            Drafting
          </span>
        );
      case "unassigned":
        return (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
            Unassigned
          </span>
        );
    }
  };

  const hasActiveFilters =
    categoryFilter !== "all" ||
    statusFilter !== "all" ||
    assigneeFilter !== "all" ||
    searchQuery.trim() !== "" ||
    selectedMemberEmail !== null ||
    isUnassignedFiltered;

  const clearAllFilters = () => {
    setCategoryFilter("all");
    setStatusFilter("all");
    setAssigneeFilter("all");
    setSearchQuery("");
    if (onClearMemberFilter) onClearMemberFilter();
    if (onClearUnassignedFilter) onClearUnassignedFilter();
  };

  return (
    <div className="space-y-4">
      {/* Header Notification for Auto-Init */}
      {hasUninitializedGroups && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#2ba5b5]/30 bg-[#2ba5b5]/10 p-3.5 text-xs dark:bg-[#2ba5b5]/15">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 shrink-0 text-[#2ba5b5]" />
            <div>
              <span className="font-semibold text-slate-900 dark:text-white">
                Initialize Proposal Response Outline
              </span>
              <p className="text-[11px] text-slate-600 dark:text-slate-300">
                You have requirements grouped into sections that haven't been locked into drafting
                packages yet. Initialize them to track word budgets, leads, and reviewers.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleAutoInit}
            disabled={isInitializing}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97] disabled:opacity-50"
          >
            {isInitializing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            <span>Lock In Sections</span>
          </button>
        </div>
      )}

      {initSuccessMessage && (
        <div className="rounded-lg bg-emerald-50 p-2.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
          {initSuccessMessage}
        </div>
      )}

      {/* Toolbar: Filters, Search & Add Section */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sections or requirements..."
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-800 dark:bg-slate-900 dark:text-white"
            />
          </div>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
          >
            <option value="all">All Categories</option>
            <option value="technical">Technical</option>
            <option value="management">Management & Staffing</option>
            <option value="qualifications">Past Performance & Quals</option>
            <option value="pricing">Pricing</option>
            <option value="compliance">Compliance</option>
            <option value="general">General</option>
          </select>

          {/* Assignee Filter */}
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
          >
            <option value="all">All Drafters</option>
            <option value="unassigned">Unassigned Only</option>
            {staff.map((s) => (
              <option key={s.id} value={s.email}>
                {s.displayName}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"
          >
            <option value="all">All Statuses</option>
            <option value="unassigned">Unassigned</option>
            <option value="draft">Drafting</option>
            <option value="review">In Review</option>
            <option value="approved">Approved</option>
          </select>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs text-[#2ba5b5] hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingSection(null);
            setIsEditModalOpen(true);
          }}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97]"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add Section</span>
        </button>
      </div>

      {/* Sections List */}
      <div className="space-y-3">
        {filteredGroups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center dark:border-slate-800">
            <Layers className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
            <h4 className="mt-2 text-xs font-semibold text-slate-900 dark:text-white">
              No matching proposal sections
            </h4>
            <p className="mt-1 text-[11px] text-slate-500">
              Try adjusting your search query or filters.
            </p>
          </div>
        ) : (
          filteredGroups.map((group) => {
            const percentWords =
              group.targetWordCount > 0
                ? Math.min(Math.round((group.actualWordCount / group.targetWordCount) * 100), 100)
                : 0;

            const isCascading = cascadingMap[group.id] || false;
            const cascadeSuccess = cascadeSuccessMap[group.id] || false;

            return (
              <ExpandableCard
                key={group.id}
                title={`[${group.sectionRef}] ${group.title}`}
                badge={group.category.toUpperCase()}
                badgeColor={getCategoryColor(group.category)}
                summary={
                  <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                    <span>
                      {group.requirements.length} requirement
                      {group.requirements.length === 1 ? "" : "s"}
                    </span>
                    <span>•</span>
                    <span>
                      {group.actualWordCount.toLocaleString()} /{" "}
                      {group.targetWordCount.toLocaleString()} words
                    </span>
                    {group.dueDate && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-[#2ba5b5]" />
                          Due: {group.dueDate}
                        </span>
                      </>
                    )}
                  </div>
                }
                headerActions={
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {getStatusBadge(group.status)}

                    <button
                      type="button"
                      onClick={() => {
                        setEditingSection(group);
                        setIsEditModalOpen(true);
                      }}
                      className="btn-animate rounded-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                      title="Edit Section Settings"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>

                    {group.draftSection && (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteSection(group)}
                        className="btn-animate rounded-md border border-slate-200 p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:hover:bg-rose-950/40"
                        title="Delete Section"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                }
                defaultExpanded={false}
              >
                <div className="space-y-4 pt-2">
                  {/* Allocation Controls Row */}
                  <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3 sm:grid-cols-2 lg:grid-cols-3 dark:border-slate-800/60 dark:bg-slate-800/40">
                    {/* Drafter / Section Lead */}
                    <div>
                      <label
                        htmlFor={`lead-select-${group.id}`}
                        className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300"
                      >
                        Section Lead / Primary Drafter
                      </label>
                      <select
                        id={`lead-select-${group.id}`}
                        value={group.assignedToEmail || ""}
                        onChange={(e) => handleAssignLead(group, e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                      >
                        <option value="">Unassigned</option>
                        {staff.map((s) => (
                          <option key={s.id} value={s.email}>
                            {s.displayName}
                          </option>
                        ))}
                      </select>

                      {/* Cascade action */}
                      {group.assignedToEmail && group.requirements.length > 0 && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleCascade(group)}
                            disabled={isCascading}
                            className="btn-animate inline-flex items-center gap-1 text-[10px] font-medium text-[#2ba5b5] hover:underline disabled:opacity-50"
                          >
                            <ArrowDownToLine className="h-2.5 w-2.5" />
                            <span>
                              {isCascading
                                ? "Cascading..."
                                : `Cascade drafter to ${group.requirements.length} requirement${group.requirements.length === 1 ? "" : "s"}`}
                            </span>
                          </button>
                          {cascadeSuccess && (
                            <span className="flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                              <Check className="h-2.5 w-2.5" /> Done
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* SME Reviewer */}
                    <div>
                      <label
                        htmlFor={`reviewer-select-${group.id}`}
                        className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300"
                      >
                        SME Reviewer
                      </label>
                      <select
                        id={`reviewer-select-${group.id}`}
                        value={group.reviewerEmail || ""}
                        onChange={(e) => handleAssignReviewer(group, e.target.value)}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                      >
                        <option value="">No Reviewer Assigned</option>
                        {staff.map((s) => (
                          <option key={s.id} value={s.email}>
                            {s.displayName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Word Budget Progress */}
                    <div>
                      <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                        <span>Word Count Budget</span>
                        <span className="text-[10px] text-slate-500 font-normal">
                          {percentWords}%
                        </span>
                      </div>
                      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                        <div
                          className="h-full rounded-full bg-[#2ba5b5]"
                          style={{ width: `${percentWords}%` }}
                        />
                      </div>
                      <p className="mt-1 text-[10px] text-slate-500">
                        {group.actualWordCount.toLocaleString()} /{" "}
                        {group.targetWordCount.toLocaleString()} words budgeted
                      </p>
                    </div>
                  </div>

                  {/* Strategy Notes */}
                  {group.notes && (
                    <div className="rounded-lg border border-slate-100 bg-amber-50/40 p-2.5 text-xs dark:border-slate-800 dark:bg-amber-950/20">
                      <span className="font-semibold text-amber-900 dark:text-amber-200">
                        Strategy & Win Themes:{" "}
                      </span>
                      <p className="mt-0.5 text-slate-700 dark:text-slate-300">{group.notes}</p>
                    </div>
                  )}

                  {/* Linked Requirements List */}
                  <div>
                    <h5 className="text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                      Linked Requirements ({group.requirements.length})
                    </h5>
                    <div className="mt-1.5 divide-y divide-slate-100 rounded-lg border border-slate-100 bg-white text-xs dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
                      {group.requirements.length === 0 ? (
                        <p className="p-3 text-[11px] italic text-slate-400">
                          No specific requirements linked to this section yet.
                        </p>
                      ) : (
                        group.requirements.map((req) => (
                          <div
                            key={req.id}
                            className="flex flex-wrap items-center justify-between gap-2 p-2.5 hover:bg-slate-50/50 dark:hover:bg-slate-800/40"
                          >
                            <div className="flex items-center gap-2">
                              {req.confirmed ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                              ) : (
                                <div className="h-3.5 w-3.5 rounded-full border border-slate-300 dark:border-slate-600" />
                              )}
                              <span className="font-medium text-slate-900 dark:text-white">
                                {req.title}
                              </span>
                              {req.mandatory && (
                                <span className="rounded-md bg-rose-50 px-1.5 py-0.2 text-[9px] font-semibold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
                                  Mandatory
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-2 text-[10px] text-slate-500">
                              <span>Assignee: {req.assignedTo || "Unassigned"}</span>
                              <button
                                type="button"
                                onClick={() => setActiveSmeReq(req)}
                                className="btn-animate inline-flex items-center gap-1 text-[#2ba5b5] hover:underline"
                              >
                                <HelpCircle className="h-3 w-3" />
                                <span>Consult SME</span>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </ExpandableCard>
            );
          })
        )}
      </div>

      {/* Edit Section Modal */}
      {isEditModalOpen && (
        <EditSectionModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingSection(null);
          }}
          section={editingSection}
          onSave={handleSaveSection}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteSection && (
        <Modal
          isOpen={Boolean(confirmDeleteSection)}
          onClose={() => setConfirmDeleteSection(null)}
          title="Delete Proposal Section"
        >
          <div className="space-y-3">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Are you sure you want to delete proposal section{" "}
              <strong>&ldquo;{confirmDeleteSection.title}&rdquo;</strong>? Underlying requirements
              will not be deleted, but draft content and section metadata will be removed.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmDeleteSection(null)}
                className="btn-animate rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {isDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <span>Confirm Delete Section</span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Ask SME Consultation Modal */}
      {activeSmeReq && (
        <AskSmeModal
          isOpen={Boolean(activeSmeReq)}
          onClose={() => setActiveSmeReq(null)}
          requirement={activeSmeReq}
          opportunity={opportunity}
        />
      )}
    </div>
  );
};
